import './framework.scss';

import React from 'react';
import { graphql, Link, navigate } from 'gatsby';

import * as Core from 'components/core';

import Layout from 'components/layout/Layout';
import FrameworkGraph from 'components/tree-diagrams/FrameworkGraph';
import DomainBreakdown from 'components/framework/DomainBreakdown';
import DomainsLegend from 'components/framework/DomainsLegend';
import {
  CompareDomainsLink,
  CompareFrameworksLink,
  CompareTermsLink
} from 'components/VisualToolLink';
import FrameworksGrid from 'components/FrameworksGrid';

import { Framework, Term } from 'types';

interface Props {
  data: {
    framework: Framework;
    allFrameworks: {
      frameworks: Framework[];
    };
    allTerms: {
      terms: Term[];
    };
    domainEmphasis: {
      domains: {
        identifier: string;
        count: number;
      }[];
    } | null;
  };
}

export default class FrameworkPage extends React.Component<Props> {
  get terms(): Term[] {
    const frameworkID = this.props.data.framework.identifier;
    return this.props.data.allTerms.terms.filter(
      (t) => t.frameworkID === frameworkID
    );
  }

  selectAssocatedFramework = (
    event: React.SyntheticEvent<HTMLSelectElement>
  ) => {
    const frameworkID = event.currentTarget.value;
    if (frameworkID != null) {
      navigate(`/frameworks/${frameworkID}/`);
    }
  };

  render() {
    const { framework } = this.props.data;
    const { frameworks } = this.props.data.allFrameworks;
    const associatedFrameworks = frameworks.filter(
      (f) => f.group === framework.group
    );
    const { terms } = this.props.data.allTerms;
    const domainEmphasis =
      this.props.data.domainEmphasis && this.props.data.domainEmphasis.domains;

    return (
      <Layout>
        <div className="FrameworkPage">
          <div className="FrameworkPage-body">
            <div className="FrameworkPage-content">
              {associatedFrameworks.length > 1 && (
                <div className="FrameworkPage-associatedFrameworks">
                  <h3>Select an age range</h3>
                  <Core.Select
                    value=""
                    onChange={this.selectAssocatedFramework}
                    placeholder="Choose related framework"
                  >
                    {associatedFrameworks.map((associatedFramework) => (
                      <option
                        key={associatedFramework.identifier}
                        value={associatedFramework.identifier}
                        disabled={
                          associatedFramework.identifier ===
                          framework.identifier
                        }
                      >
                        {associatedFramework.name}
                      </option>
                    ))}
                  </Core.Select>
                </div>
              )}

              <h1>{framework.name}</h1>
              {framework.name !== framework.fullName && (
                <h2 className="FrameworkPage-content-fullName">
                  {framework.fullName}
                </h2>
              )}

              {this.terms.length > 0 && (
                <>
                  <p>
                    <Link to={`/frameworks/${framework.identifier}/terms`}>
                      View All Term Definitions
                    </Link>
                  </p>

                  <div className="FrameworkPage-treeDiagram">
                    <FrameworkGraph framework={framework} terms={this.terms} />
                  </div>
                </>
              )}

              {domainEmphasis && (
                <div>
                  <h2>Breakdown by Domain</h2>
                  <hr />
                  <h4>Domain Key</h4>
                  <DomainsLegend />
                  <DomainBreakdown domains={domainEmphasis} />
                </div>
              )}
            </div>
            <div className="FrameworkPage-sidebar-wrapper">
              <div className="FrameworkPage-sidebar-links">
                <CompareDomainsLink displayStyle="thumbnail" />
                <CompareFrameworksLink
                  displayStyle="thumbnail"
                  state={{ frameworkID: framework.identifier }}
                />
                <CompareTermsLink displayStyle="thumbnail" />
              </div>
            </div>
          </div>

          <div>
            <h3>Explore other Frameworks</h3>
            <FrameworksGrid
              frameworks={frameworks}
              terms={terms}
              excludedFramework={framework}
            />
          </div>
        </div>
      </Layout>
    );
  }
}

// OPTIMIZE: Filter the terms to only those in this framework
export const query = graphql`
  query FrameworkPage($identifier: Int!) {
    framework: frameworksJson(identifier: { eq: $identifier }) {
      identifier
      group
      name
      fullName
      color
    }
    allFrameworks: allFrameworksJson {
      frameworks: nodes {
        identifier
        group
        name
        fullName
        color
      }
    }
    allTerms: allTermsJson {
      terms: nodes {
        identifier
        name
        frameworkID
        frameworkName
        parentID
        tier
        definition
        includeInThesaurus
      }
    }
    domainEmphasis: frameworkDomainEmphasisJson(
      identifier: { eq: $identifier }
    ) {
      domains {
        identifier
        count
      }
    }
  }
`;
